import type { RuleResponse, RuleArray } from '../types'
import { passwordValidation } from '../utils/passwordValidation'
import { useCoreConfig } from '#imports'

export const useCoreRules = () => {
    const { translations: { validationRules: validationMessage } } = useCoreConfig()

    const required = (message: string = validationMessage.required.default) => {
        return (value?: string | number | boolean): RuleResponse => {
            if (value) return true

            return message
        }
    }

    const minLength = (minVal: number, message: string) => {
        return (value?: string): RuleResponse => {
            if (!value) return true

            return value.length >= minVal || message
        }
    }

    /**
     * Validate a string is an email.
     *
     * Regex is sourced from this SO discussion: https://stackoverflow.com/a/46181
     *
     * @param value An email
     * @returns true or a string
     */
    function email(value?: string): RuleResponse {
        if (!value) return true

        return !!value.match(/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/) || validationMessage.email.invalid
    }

    function password(value?: string): RuleResponse {
        if (!value) return true

        const validationObject = passwordValidation(value)

        return validationObject.isValid || validationMessage.password.invalid
    }

    function passwordOld(value?: string): RuleResponse {
        if (!value) return true

        if (value.length < 8) return validationMessage.password.tooShort

        return true
    }

    const between = (minVal: number, maxVal: number, message: string) => {
        return (value?: string): RuleResponse => {
            if (!value) return true

            return (value.length >= minVal && value.length <= maxVal) || message
        }
    }

    const mustDiffer = (current: () => string | undefined, message: string) => {
        return (value?: string): RuleResponse => {
            const currentValue = current()
            if (!value || !currentValue) return true
            return value !== currentValue || message
        }
    }

    const noNumbers = (value: string): RuleResponse => !value.match(/\d+/g) || validationMessage.noNumbers

    // Allows unicode letters (including æøå, accented chars), whitespace, hyphens and apostrophes
    const noSpecialCharacters = (value: string): RuleResponse => !!value.match(/^[\p{L}\s'-]*$/u) || validationMessage.noSpecialChars

    const EMAIL: RuleArray = [
        required(validationMessage.required.email),
        email,
    ]

    const NEW_PASSWORD: RuleArray = [
        required(validationMessage.required.newPassword),
        password,
    ]

    const EXISTING_PASSWORD: RuleArray = [
        required(validationMessage.required.password),
        passwordOld,
    ]

    return {
        required,
        minLength,
        email,
        password,
        passwordOld,
        between,
        mustDiffer,
        noNumbers,
        noSpecialCharacters,
        EMAIL,
        EXISTING_PASSWORD,
        NEW_PASSWORD,
    }
}
